"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import type { ApiWaveDecisionPause } from "@/generated/models/ApiWaveDecisionPause";
import type { TimeLeft } from "@/helpers/waves/time.utils";
import { CompactTimeCountdown } from "./CompactTimeCountdown";
interface TimelineToggleHeaderProps {
  readonly isOpen: boolean;
  readonly setIsOpen: (isOpen: boolean) => void;
  readonly nextDecisionTime: number | null;
  readonly timeLeft: TimeLeft;
  readonly isPaused?: boolean | undefined;
  readonly currentPause?: ApiWaveDecisionPause | null | undefined;
}

/**
 * Renders the header for the timeline with toggle functionality
 */
export const TimelineToggleHeader: FC<TimelineToggleHeaderProps> = t0 => {
  const $ = _c(30);
  const {
    isOpen,
    setIsOpen,
    nextDecisionTime,
    timeLeft,
    isPaused: t1,
    currentPause
  } = t0;
  const isPaused = t1 === undefined ? false : t1;
  const hasNextDecision = typeof nextDecisionTime === "number";
  let t2;
  if ($[0] !== hasNextDecision || $[1] !== nextDecisionTime) {
    t2 = hasNextDecision ? new Date(nextDecisionTime).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }) : null;
    $[0] = hasNextDecision;
    $[1] = nextDecisionTime;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formattedNextDecisionDate = t2;
  let t3;
  if ($[3] !== currentPause || $[4] !== formattedNextDecisionDate || $[5] !== hasNextDecision || $[6] !== isPaused || $[7] !== nextDecisionTime || $[8] !== timeLeft) {
    t3 = () => {
      if (isPaused && currentPause) {
        return <span className="tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-md tw-border tw-border-solid tw-border-iron-600/40 tw-bg-gradient-to-r tw-from-iron-800/90 tw-to-iron-700/70 tw-px-3 tw-py-1.5 tw-text-xs tw-shadow-sm tw-transition-all tw-duration-300 tw-ease-out group-hover:tw-border-iron-600/30 group-hover:tw-from-iron-700/60 group-hover:tw-to-iron-600/40 group-hover:tw-shadow-none sm:tw-gap-x-2.5"><span className="tw-whitespace-nowrap tw-bg-gradient-to-r tw-from-amber-200 tw-via-amber-100 tw-to-amber-200/90 tw-bg-clip-text tw-font-bold tw-text-transparent">Decisions paused</span><span className="tw-text-iron-500">•</span><span className="tw-whitespace-nowrap tw-font-medium tw-text-iron-300">{hasNextDecision ? `Next decision after ${new Date(nextDecisionTime).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric"
            })}` : "No decision scheduled"}</span></span>;
      }
      if (hasNextDecision) {
        return <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-x-4 tw-gap-y-1"><CompactTimeCountdown timeLeft={timeLeft} /><span className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-iron-300">{formattedNextDecisionDate}</span></div>;
      }
      return <span className="tw-text-xs tw-text-iron-400">No upcoming events</span>;
    };
    $[3] = currentPause;
    $[4] = formattedNextDecisionDate;
    $[5] = hasNextDecision;
    $[6] = isPaused;
    $[7] = nextDecisionTime;
    $[8] = timeLeft;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const getStatusDisplay = t3;
  let t4;
  if ($[10] !== isOpen || $[11] !== setIsOpen) {
    t4 = () => setIsOpen(!isOpen);
    $[10] = isOpen;
    $[11] = setIsOpen;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = `tw-flex-shrink-0 tw-whitespace-nowrap tw-text-xs tw-font-semibold ${hasNextDecision ? "tw-text-iron-100" : "tw-text-iron-400"}`;
  const t6 = hasNextDecision ? "Decision Timeline" : "Announcement history";
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== getStatusDisplay) {
    t8 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-end tw-text-xs tw-font-medium">{getStatusDisplay()}</div>;
    $[16] = getStatusDisplay;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = isOpen ? "Collapse" : "Expand";
  const t10 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 desktop-hover:group-hover:tw-text-iron-100 ${isOpen ? "tw-rotate-180" : ""} tw-transition-all tw-duration-300 tw-ease-in-out`;
  let t11;
  if ($[18] !== t10) {
    t11 = <FontAwesomeIcon icon={faChevronDown} className={t10} />;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t9) {
    t12 = <button className="tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-iron-600/40 tw-bg-iron-700/50 tw-transition-all tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/50 desktop-hover:hover:tw-bg-iron-600/60" aria-label={t9}>{t11}</button>;
    $[20] = t11;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t7 || $[25] !== t8) {
    t13 = <div className="tw-flex tw-w-full tw-items-center tw-gap-2">{t7}{t8}{t12}</div>;
    $[23] = t12;
    $[24] = t7;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== t4) {
    t14 = <div className="tw-group tw-cursor-pointer tw-rounded-t-lg tw-border tw-border-solid tw-border-iron-700/50 tw-bg-iron-800/95 tw-px-3 tw-py-1.5 tw-shadow-sm tw-transition-all tw-duration-300 tw-ease-out tw-@container desktop-hover:hover:tw-bg-iron-700/80" onClick={t4}>{t13}</div>;
    $[27] = t13;
    $[28] = t4;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
};